import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FilterChipData } from './FilterChipsContainer';
import {
  selectAudiences,
  selectProductGroups,
  selectCategories,
  selectFeatures,
  selectEvents,
  selectProductFilter,
  selectLanesTiers,
  selectReleases,
  selectCategoryToProductMap,
  selectGenAiFilter,
  selectRevenueImpactFilter,
  selectStatus,
  selectMomentTypeFilter
} from 'app/pages/Home/slice/selectors';
import type { RootState } from 'types';
import { useHomeSlice } from 'app/pages/Home/slice';
import { useRsdFormsSlice } from 'app/components/RsdForms/store/slice';
import { saveUserPreferences } from 'utils/helpers';
import { MOMENT_TYPES } from 'utils/constants';

export interface FilterChipGroup {
  type: string;
  chips: FilterChipData[];
}

export interface UseFilterChipsOptions {
  /** Omit End of Life (EOL) moment-type chips (milestones grid report). */
  hideEolMomentType?: boolean;
}

export function useFilterChips(
  options: UseFilterChipsOptions = {}
): FilterChipGroup[] {
  const { hideEolMomentType = false } = options;
  useRsdFormsSlice();
  const { actions } = useHomeSlice();
  const dispatch = useDispatch();

  // Get all the filter data
  const audiences = useSelector(selectAudiences);
  const productGroups = useSelector(selectProductGroups);
  const categories = useSelector(selectCategories);
  const features = useSelector(selectFeatures);
  const events = useSelector(selectEvents);
  const products = useSelector(selectProductFilter);
  const releases = useSelector(selectReleases);
  const categoryToProductMap = useSelector(selectCategoryToProductMap);
  const genAiFilter = useSelector(selectGenAiFilter);
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const status = useSelector(selectStatus);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);
  const rsdMomentTypes = useSelector(
    (s: RootState) => s.rsdForms?.momentTypes ?? []
  );

  // Create a map of event IDs to titles
  const eventIdToTitleMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (releases?.releaseScheduled) {
      const eventsData = releases.releaseScheduled.find(release =>
        release.type.includes('Events')
      );

      if (eventsData?.projects) {
        const allProjects = eventsData.projects.reduce((acc, quarterData) => {
          if (quarterData.project && Array.isArray(quarterData.project)) {
            return [...acc, ...quarterData.project];
          }
          return acc;
        }, []);

        allProjects.forEach(project => {
          if (project.issueId && project.title) {
            map[project.issueId] = project.title;
          }
        });
      }
    }
    return map;
  }, [releases?.releaseScheduled]);

  return useMemo(() => {
    const groups: FilterChipGroup[] = [];

    // Lanes/Tiers chips are excluded from header display
    // They are still available in the filter menu but won't show as chips in the header

    // Add audience chips
    const audienceList = audiences?.selectedAudiences || [];
    if (audienceList.length > 0) {
      const audienceChips: FilterChipData[] = [];
      audienceList.forEach(audience => {
        if (audience !== 'all') {
          audienceChips.push({
            id: `audience-${audience}`,
            label: audience,
            variant: 'audience',
            onRemove: () => {
              // Remove specific audience from selection
              const updatedAudiences = audienceList.filter(a => a !== audience);
              dispatch(actions.setSelectedAudiences(updatedAudiences));
              saveUserPreferences({ audiences: updatedAudiences });

              // ✅ Clear all highlights when removing filter chip
              dispatch(actions.clearHighlightedIssues());
            }
          });
        }
      });
      if (audienceChips.length > 0) {
        groups.push({ type: 'audience', chips: audienceChips });
      }
    }

    // Add product group chips
    const productGroupList = productGroups?.selectedGroups || [];
    if (productGroupList.length > 0) {
      const productGroupChips: FilterChipData[] = [];
      productGroupList.forEach(group => {
        if (group !== 'all') {
          productGroupChips.push({
            id: `productGroup-${group}`,
            label: group,
            variant: 'productGroup',
            onRemove: () => {
              const updatedGroups = productGroupList.filter(g => g !== group);
              dispatch(actions.setSelectedGroups(updatedGroups));
              saveUserPreferences({ productGroups: updatedGroups });

              // ✅ Clear all highlights when removing filter chip
              dispatch(actions.clearHighlightedIssues());
            }
          });
        }
      });
      if (productGroupChips.length > 0) {
        groups.push({ type: 'productGroup', chips: productGroupChips });
      }
    }

    // Add category chips
    const categoryList = categories?.selectedCats || [];
    if (categoryList.length > 0) {
      const categoryChips: FilterChipData[] = [];
      categoryList.forEach(category => {
        if (category !== 'all') {
          categoryChips.push({
            id: `category-${category}`,
            label: category,
            variant: 'category',
            onRemove: () => {
              const updatedCategories = categoryList.filter(
                c => c !== category
              );
              let updatedProducts = [...(products?.selectedProducts || [])];

              // Remove all products belonging to the removed category
              const categoryProducts = categoryToProductMap[category] || [];
              updatedProducts = updatedProducts.filter(
                prod => !categoryProducts.includes(prod)
              );

              // Update both old state and new categoryProductFilter
              dispatch(actions.setSelectedCats(updatedCategories));
              dispatch(actions.setSelectedProducts(updatedProducts));
              dispatch(
                actions.setCategoryProductSelection({
                  categories: updatedCategories,
                  products: updatedProducts
                })
              );

              saveUserPreferences({
                categories: updatedCategories,
                products: updatedProducts,
                categoryProductCategories: updatedCategories,
                categoryProductProducts: updatedProducts
              });

              // ✅ Clear all highlights when removing filter chip
              dispatch(actions.clearHighlightedIssues());
            }
          });
        }
      });
      if (categoryChips.length > 0) {
        groups.push({ type: 'category', chips: categoryChips });
      }
    }

    // Add product chips - only show products that are NOT part of a fully selected category
    const productList = products?.selectedProducts || [];
    const selectedCategoryList = categories?.selectedCats || [];

    if (productList.length > 0) {
      const productChips: FilterChipData[] = [];

      // Find which products belong to fully selected categories
      const productsInFullySelectedCategories = new Set<string>();
      selectedCategoryList.forEach(category => {
        const categoryProducts = categoryToProductMap[category] || [];
        if (categoryProducts.length > 0) {
          // Check if ALL products of this category are selected
          const allProductsSelected = categoryProducts.every(prod =>
            productList.includes(prod)
          );
          if (allProductsSelected) {
            // Add all products of this fully selected category to the set
            categoryProducts.forEach(prod =>
              productsInFullySelectedCategories.add(prod)
            );
          }
        }
      });

      // Only show product chips for products NOT in fully selected categories
      productList.forEach(product => {
        if (
          product !== 'all' &&
          !productsInFullySelectedCategories.has(product)
        ) {
          productChips.push({
            id: `product-${product}`,
            label: product,
            variant: 'product',
            onRemove: () => {
              const updatedProducts = productList.filter(p => p !== product);
              let updatedCategories = [...(categories?.selectedCats || [])];

              // Check if any category should be deselected due to removing this product
              // A category should remain selected only if ALL its products are still selected
              Object.entries(categoryToProductMap).forEach(
                ([category, categoryProducts]) => {
                  if (categoryProducts && categoryProducts.length > 0) {
                    const allProductsStillSelected = categoryProducts.every(
                      prod => updatedProducts.includes(prod)
                    );
                    if (
                      !allProductsStillSelected &&
                      updatedCategories.includes(category)
                    ) {
                      // Remove category if not all its products are selected
                      updatedCategories = updatedCategories.filter(
                        c => c !== category
                      );
                    }
                  }
                }
              );

              // Update both old state and new categoryProductFilter
              dispatch(actions.setSelectedProducts(updatedProducts));
              dispatch(actions.setSelectedCats(updatedCategories));
              dispatch(
                actions.setCategoryProductSelection({
                  categories: updatedCategories,
                  products: updatedProducts
                })
              );

              saveUserPreferences({
                products: updatedProducts,
                categories: updatedCategories,
                categoryProductProducts: updatedProducts,
                categoryProductCategories: updatedCategories
              });

              // ✅ Clear all highlights when removing filter chip
              dispatch(actions.clearHighlightedIssues());
            }
          });
        }
      });

      if (productChips.length > 0) {
        groups.push({ type: 'product', chips: productChips });
      }
    }

    // Add event chips
    const eventList = events?.selectedEvents || [];
    if (eventList.length > 0) {
      const eventChips: FilterChipData[] = [];
      eventList.forEach(eventId => {
        if (eventId !== 'All') {
          // Look up the event title from the ID
          const eventTitle = eventIdToTitleMap[eventId] || eventId;
          eventChips.push({
            id: `event-${eventId}`,
            label: eventTitle,
            variant: 'event',
            onRemove: () => {
              const updatedEvents = eventList.filter(e => e !== eventId);
              dispatch(actions.setSelectedEvents(updatedEvents));
              saveUserPreferences({ events: updatedEvents });

              // ✅ If this is the last event being removed, clear highlights
              // Check if other filters (surfaces, GenAI) are active before clearing
              if (updatedEvents.length === 0) {
                const surfacesActive = features?.selectedFeatures?.length > 0;
                const genAiActive = genAiFilter?.selectedFilters?.length > 0;

                // Clear highlights when last event is removed
                dispatch(actions.clearHighlightedIssues());

                // Only set to nested state if no other filters requiring unnesting are active
                if (!surfacesActive && !genAiActive) {
                  dispatch(actions.setUnNestAll(false));
                }
              } else {
                // ✅ Clear all highlights when removing an event chip (not the last one)
                dispatch(actions.clearHighlightedIssues());
              }
            }
          });
        }
      });
      if (eventChips.length > 0) {
        groups.push({ type: 'event', chips: eventChips });
      }
    }

    // Add feature chips
    const featureList = features?.selectedFeatures || [];
    if (featureList.length > 0) {
      const featureChips: FilterChipData[] = [];
      featureList.forEach(feature => {
        if (feature !== 'all') {
          featureChips.push({
            id: `feature-${feature}`,
            label: feature,
            variant: 'feature',
            onRemove: () => {
              const updatedFeatures = featureList.filter(f => f !== feature);
              dispatch(actions.setSelectedFeatures(updatedFeatures));
              saveUserPreferences({ features: updatedFeatures });

              // ✅ Clear all highlights when removing filter chip
              if (updatedFeatures.length === 0) {
                dispatch(actions.clearHighlightedIssues());
                dispatch(actions.setUnNestAll(false));
                dispatch(actions.clearPreviousUnNestAllState());
              } else {
                // Still have features selected, just clear highlights
                dispatch(actions.clearHighlightedIssues());
              }
            }
          });
        }
      });
      if (featureChips.length > 0) {
        groups.push({ type: 'feature', chips: featureChips });
      }
    }

    // Add Gen AI filter chips
    const genAiFilterList = genAiFilter?.selectedFilters || [];
    if (genAiFilterList.length > 0) {
      const genAiChips: FilterChipData[] = [];

      // Create friendly labels for Gen AI filters
      const genAiLabels: Record<string, string> = {
        'all-features': 'All GenAI Features',
        'consumes-credits': 'Consumes Credits'
      };

      genAiFilterList.forEach(filterKey => {
        const label = genAiLabels[filterKey] || filterKey;
        genAiChips.push({
          id: `genai-${filterKey}`,
          label: label,
          variant: 'genAi',
          onRemove: () => {
            const updatedFilters = genAiFilterList.filter(f => f !== filterKey);
            dispatch(actions.setGenAiFilters(updatedFilters));
            // ✅ Save to localStorage so filter state persists across page refreshes
            saveUserPreferences({ genAiFilters: updatedFilters });

            // ✅ Clear all highlights when removing filter chip
            if (updatedFilters.length === 0) {
              dispatch(actions.clearHighlightedIssues());
              dispatch(actions.setUnNestAll(false));
              dispatch(actions.clearPreviousUnNestAllState());
            } else {
              // Still have GenAI filters selected, just clear highlights
              dispatch(actions.clearHighlightedIssues());
            }
          }
        });
      });

      if (genAiChips.length > 0) {
        groups.push({ type: 'genAi', chips: genAiChips });
      }
    }

    // Add Revenue Impact filter chips
    const revenueImpactFilterList = revenueImpactFilter?.selectedFilters || [];
    if (revenueImpactFilterList.length > 0) {
      const revenueImpactChips: FilterChipData[] = [];

      // Create friendly labels for Revenue Impact filters
      const revenueImpactLabels: Record<string, string> = {
        'revenue-impact-yes': 'Yes',
        'revenue-impact-no': 'No'
      };

      revenueImpactFilterList.forEach(filterKey => {
        const label = revenueImpactLabels[filterKey] || filterKey;
        revenueImpactChips.push({
          id: `revenue-impact-${filterKey}`,
          label: label,
          variant: 'revenueImpact',
          onRemove: () => {
            const updatedFilters = revenueImpactFilterList.filter(
              f => f !== filterKey
            );
            dispatch(actions.setRevenueImpactFilters(updatedFilters));
            // ✅ Save to localStorage so filter state persists across page refreshes
            saveUserPreferences({ revenueImpactFilters: updatedFilters });
          }
        });
      });

      if (revenueImpactChips.length > 0) {
        groups.push({ type: 'revenueImpact', chips: revenueImpactChips });
      }
    }

    // Add Status filter chips
    const statusList = status?.selectedStatus || [];
    if (statusList.length > 0) {
      const statusChips: FilterChipData[] = [];

      // Create friendly labels for Status filters
      const statusLabels: Record<string, string> = {
        'risk-level-low': 'Risk Level : Low',
        'risk-level-medium': 'Risk Level : Medium',
        'risk-level-high': 'Risk Level : High',
        'in-planning': 'In Planning'
      };

      statusList.forEach(statusKey => {
        if (statusKey !== 'all') {
          const label = statusLabels[statusKey] || statusKey;
          statusChips.push({
            id: `status-${statusKey}`,
            label: label,
            variant: 'status',
            onRemove: () => {
              const updatedStatuses = statusList.filter(s => s !== statusKey);
              dispatch(actions.setSelectedStatus(updatedStatuses));
              // ✅ Save to localStorage so filter state persists across page refreshes
              saveUserPreferences({ status: updatedStatuses });
            }
          });
        }
      });

      if (statusChips.length > 0) {
        groups.push({ type: 'status', chips: statusChips });
      }
    }

    const momentTypeList = momentTypeFilter?.selectedMomentTypes || [];
    const eolMomentTypeId = hideEolMomentType
      ? rsdMomentTypes.find(m => m.name === MOMENT_TYPES.END_OF_LIFE)?.id
      : undefined;
    const visibleMomentTypeList = eolMomentTypeId
      ? momentTypeList.filter(id => id !== eolMomentTypeId)
      : momentTypeList;
    if (visibleMomentTypeList.length > 0) {
      const momentTypeChips: FilterChipData[] = [];
      visibleMomentTypeList.forEach(typeId => {
        if (typeId === 'all') return;
        const label = rsdMomentTypes.find(m => m.id === typeId)?.name ?? typeId;
        momentTypeChips.push({
          id: `moment-type-${typeId}`,
          label,
          variant: 'momentType',
          onRemove: () => {
            const updated = momentTypeList.filter(id => id !== typeId);
            dispatch(actions.setSelectedMomentTypes(updated));
            saveUserPreferences({ momentTypeFilters: updated });
            dispatch(actions.clearHighlightedIssues());
          }
        });
      });
      if (momentTypeChips.length > 0) {
        groups.push({ type: 'momentType', chips: momentTypeChips });
      }
    }

    return groups;
  }, [
    audiences,
    productGroups,
    categories,
    products,
    events,
    features,
    genAiFilter,
    revenueImpactFilter,
    status,
    momentTypeFilter,
    rsdMomentTypes,
    hideEolMomentType,
    categoryToProductMap,
    eventIdToTitleMap,
    dispatch,
    actions
  ]);
}
